import { ListCheckIcon } from "lucide-react";
import { use, useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { ListPreview } from "@/components/canvas-editor/components/ListPreview";
import { SearchListButton } from "@/components/canvas-editor/components/ListSearch";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ListMemberShape,
  listMemberType,
  inclusionOperators,
  InclusionOperator,
  ListMemberProps,
  Dimensions,
  inclusionOperatorOptions,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import {
  CustomNodeEditorContext,
  MIN_H,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";


export function isListMemberShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is ListMemberShape {
  return shape.type === listMemberType;
}

export class ListMemberTool extends ToolStateNode {
  static override id = listMemberType;

  static override uiToolDef = (editor: Editor) => ({
    id: listMemberType,
    icon: ListCheckIcon,
    label: ListMemberShapeUtil.docs.title,
    kbd: "l",
    defaultColor: ListMemberShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(listMemberType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={ListMember}
        util={ListMemberShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: listMemberType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class ListMemberShapeUtil extends BindableUtil<ListMemberShape> {
  static override migrations = createMigrations(listMemberType);
  static override type = listMemberType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Member of List",
    description: "Include or exclude the users in a list from the feed.",
  };

  override getType() {
    return listMemberType;
  }

  static override props: RecordProps<ListMemberShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    listUrl: T.string,
    operator: T.literalEnum(...inclusionOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 500,
      minW: 600,
      h: MIN_H,
      listUrl: "https://bsky.app/profile/profiledelta.com/lists/3limj7ouwje2a",
      operator: inclusionOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: ListMemberShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <ListMember
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function ListMember({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: ListMemberShape;
  className?: string;
  updateShape: (
    shape: ListMemberShape,
    props: Partial<ListMemberProps>,
  ) => void;
}) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const isReadonly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={ListMemberShapeUtil.docs} />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {!isReadonly && (
              <div className="flex gap-2">
                <CanvasSimpleSelect
                  aria-label="Inclusion Operator"
                  className="w-28"
                  value={shape.props.operator}
                  onValueChange={(v) =>
                    updateShape(shape, { operator: v as InclusionOperator })
                  }
                  options={inclusionOperatorOptions}
                />
                <SimpleTooltip message="The URL of the list">
                  <CanvasInput
                    aria-label="List URL"
                    value={shape.props.listUrl}
                    className="flex-1"
                    placeholder="List Member URL"
                    onValueChange={(listUrl) => updateShape(shape, { listUrl })}
                    onInput={(e) => {
                      if (
                        e.currentTarget.value.match(
                          /^https:\S+\/lists\/\S+$/,
                        ) ||
                        e.currentTarget.value.match(
                          /^at:\S+\/app\.bsky\.graph\.list\/\S+$/,
                        )
                      ) {
                        e.currentTarget.setCustomValidity("");
                      } else if (!isCustomNodeEditor) {
                        e.currentTarget.setCustomValidity(
                          "Please enter a valid list URL.",
                        );
                        e.currentTarget.reportValidity();
                      }
                    }}
                  />
                </SimpleTooltip>
                <SearchListButton
                  type="list"
                  selectUrl={(url) => {
                    updateShape(shape, { listUrl: url });
                  }}
                />
              </div>
            )}

            <ListPreview
              listUrl={shape.props.listUrl}
              hasSeparator={!isReadonly}
              // Rewrite a pasted handle URL to its DID form once the preview has
              // resolved the list. Only when the block is editable: a read-only
              // canvas must not quietly rewrite the feed it is showing.
              onCanonicalUrl={
                isReadonly
                  ? undefined
                  : (listUrl) => updateShape(shape, { listUrl })
              }
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
